import React from 'react'
import PropTypes from 'prop-types'
import {
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  CssBaseline,
} from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import styles from './Textbox.module.scss'

const hasWindow = typeof window !== 'undefined'

const HTMLElementType = hasWindow && window.HTMLElement
  ? window.HTMLElement
  : function HTMLElement() { }

const HTMLInputElementType = hasWindow && window.HTMLInputElement
  ? window.HTMLInputElement
  : HTMLElementType

const theme = createTheme({
  typography: {
    fontFamily: '"JohnsonText", Arial, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 500,
    fontWeightBold: 700,
  },
})

export function TextboxDesktop(props) {
  const handleChange = (event) => {
    const { value } = event.target

    if (props.isNumeric) {
      if (
        /^\d*$/.test(value)
        || value === ''
        || event.nativeEvent.inputType === 'deleteContentBackward'
      ) {
        props.onChange(event)
      }
    } else {
      props.onChange(event)
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FormControl error={props.error} className={styles.desktop}>
        <InputLabel
          shrink
          htmlFor={`${props.label}-textbox`}
          className={styles['textbox-label']}
          sx={{
            '&.Mui-focused': {
              color: '#4545E0',
            },
          }}
        >
          {props.label}
        </InputLabel>
        <OutlinedInput
          id={`${props.label}-textbox`}
          type={props.type}
          placeholder={props.placeholderText}
          value={props.value}
          onChange={handleChange}
          className={`${styles[`${props.type}-textbox`]} ${styles.desktop}`}
          notched
          label={props.label}
          inputComponent={props.inputComponent}
          disabled={props.disabled}
          aria-describedby={`${props.label}-textbox-helper`}
          sx={{
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: props.borderColor,
            },
            '&.Mui-disabled': {
              background: props.disabledBackground,
            },
            borderRadius: props.borderRadius,
            ...(props.minWidth && { minWidth: props.minWidth }),
            ...(props.maxWidth && { maxWidth: props.maxWidth }),
            '&.Mui-disabled .MuiOutlinedInput-input': {
              ...(props.textAlign && { textAlign: props.textAlign }),
            },
          }}
          onBlur={props.onBlur}
          required={props.required}
          inputRef={props.inputRef}
        />
        <FormHelperText
          className={styles['textbox-support-text']}
          id={`${props.label}-textbox-helper`}
        >
          {props.error ? props.supportText : ''}
        </FormHelperText>
      </FormControl>
    </ThemeProvider>
  )
}

TextboxDesktop.propTypes = {
  label: PropTypes.string,
  type: PropTypes.oneOf(['text', 'password']),
  value: PropTypes.string,
  placeholderText: PropTypes.string,
  supportText: PropTypes.string,
  inputComponent: PropTypes.elementType,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledBackground: PropTypes.string,
  borderColor: PropTypes.string,
  borderRadius: PropTypes.string,
  minWidth: PropTypes.string,
  maxWidth: PropTypes.string,
  textAlign: PropTypes.string,
  isNumeric: PropTypes.bool,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.oneOfType([
        PropTypes.instanceOf(HTMLInputElementType),
        PropTypes.oneOf([null]),
      ]),
    }),
  ]),
}

TextboxDesktop.defaultProps = {
  label: '',
  type: 'text',
  value: '',
  placeholderText: '',
  supportText: '',
  inputComponent: null,
  onChange: () => { },
  onBlur: () => { },
  error: false,
  required: false,
  disabled: false,
  disabledBackground: '#F2E7E6',
  borderColor: '#4545E0',
  borderRadius: '4px',
  minWidth: undefined,
  maxWidth: undefined,
  textAlign: undefined,
  isNumeric: false,
  inputRef: null,
}
